// sql.js-httpvfs bootstrap.
//
// Initializes a single worker that range-fetches the SQLite database
// pages from Cloudflare R2. The DB filename rotates per deploy (we
// version-stamp it to make rollouts atomic), so we read the current
// filename from /db/db-version.json, which Pages serves alongside the
// site. R2_BASE_URL points at the public R2 bucket — same custom-domain
// concept that any later domain swap can flip via one constant.

import { createDbWorker, type WorkerHttpvfs } from "sql.js-httpvfs";

// Custom domain for the R2 bucket — Cloudflare's `pub-<hash>.r2.dev`
// URL is documented as rate-limited and "not for production". The
// custom domain rides the same R2 bucket but goes through normal CF
// edge serving (full CDN, no throttle, and CORS headers are honored
// from the bucket policy).
const R2_BASE_URL = "https://db.sightlines.hockey";

// The DB-version pointer lives on R2 (NOT Pages) so a data-only refresh — the
// expansion agent rebuilding the browser DB and pushing it to R2 — is picked up
// without a Pages redeploy. The cron build uploads db-version.json to R2 too,
// so both publishers update the same pointer. Fetched no-store + cache-busted
// since it rotates; the rotating .db filename it names is immutable and cached.
const VERSION_URL = `${R2_BASE_URL}/db-version.json`;
const PAGE_SIZE = 4096;

interface DbVersion {
  db: string;
  built_at: string;
  page_size: number;
}

let workerPromise: Promise<WorkerHttpvfs> | null = null;

/** Returns a singleton sql.js-httpvfs worker, initialized lazily.
 *  Subsequent calls reuse the same worker (and thus the same in-memory
 *  page cache), so multiple components on a page share fetches. */
export function getDbWorker(): Promise<WorkerHttpvfs> {
  if (workerPromise === null) {
    workerPromise = bootstrap();
  }
  return workerPromise;
}

async function bootstrap(): Promise<WorkerHttpvfs> {
  const versionResp = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
  if (!versionResp.ok) {
    throw new Error(
      `failed to fetch ${VERSION_URL}: HTTP ${versionResp.status}`,
    );
  }
  const version = (await versionResp.json()) as DbVersion;

  // Worker and WASM live alongside the bundle. Use import.meta.url so
  // a future deploy that mounts the static assets at a non-root path
  // (e.g. a preview branch) keeps working without code changes.
  const workerUrl = new URL("./sqlite.worker.js", import.meta.url);
  const wasmUrl = new URL("./sql-wasm.wasm", import.meta.url);

  return createDbWorker(
    [
      {
        from: "inline",
        config: {
          serverMode: "full",
          url: `${R2_BASE_URL}/${version.db}`,
          requestChunkSize: version.page_size ?? PAGE_SIZE,
        },
      },
    ],
    workerUrl.toString(),
    wasmUrl.toString(),
  );
}
